import {Injectable, NgZone} from '@angular/core';
import {ApZoneAwareObject} from './ApZoneAwareObject';

// This is a layer of abstraction on top of NgZone, that can provide more specific functionality for interacting with it
@Injectable({
  providedIn: 'root',
})
export class NgZoneService {
  static readonly ajsFactoryName = 'NgZoneService';

  constructor(
    private readonly zone: NgZone,
  ) {
  }

  wrapToRun<T>(fn: (...args: any[]) => T): (...args: any[]) => T {
    return (...innerArgs: any[]) => {
      return this.zone.run(() => {
        return fn(...innerArgs);
      });
    };
  }

  wrapToRunOutsideNg<T>(fn: (...args: any[]) => T): (...args: any[]) => T {
    return (...innerArgs: any[]) => {
      return this.zone.runOutsideAngular(() => {
        return fn(...innerArgs);
      });
    };
  }

  // This function does contextual zone invoking, to allow 'fn' to be invoked outside of the Ng Zone (if necessary),
  // while callbacks will be able to re-enter the zone properly (if necessary).
  //
  // There are 2 cases to handle:
  // 1. If we are currently in the Ng Zone, then 'fn' IS invoked in runOutsideAngular(),
  // and it will provide functions to 'fn' to be able to enter back into the Ng Zone for a callback
  // that happens inside of 'fn' (like a click handler).
  //
  // 2. If we are not currently in the Ng Zone, then 'fn' IS NOT invoked in runOutsideAngular(), because we are already outside.
  // Consequently, the functions that are then provided to 'fn'
  // don't try to re-enter the Ng Zone for a callback, instead they just invoke the callback normally.
  // This is mainly because in this case, we were not in Ng Zone to start with, so it doesn't really make sense
  // to enter it for a callback.
  contextRun<T>(fn: (zoneAwareObject: ApZoneAwareObject) => T, allowOutsideZone = false): T {
    if (!NgZone.isInAngularZone()) {
      if (!allowOutsideZone) {
        console.warn('NgZoneService.contextRun() called outside of NgZone.' +
          ' There are many cases where this makes sense, but it could be a sign of a bug, and each case is different.' +
          ' If this is a valid case, then pass true for allowOutsideZone parameter');
      }
      const zoneObj: ApZoneAwareObject = {
        runInZoneIfNecessary: <U>(callback: () => U) => callback(),
        wrapToRunInZoneIfNecessary: <U>(callback: (...args: any[]) => U) => callback,
      };
      return fn(zoneObj);
    }

    const enterZoneObj: ApZoneAwareObject = {
      runInZoneIfNecessary: <U>(callback: () => U) => this.run(callback),
      wrapToRunInZoneIfNecessary: <U>(callback: (...args: any[]) => U) => this.wrapToRun(callback),
    };
    return this.zone.runOutsideAngular(() => {
      return fn(enterZoneObj);
    });
  }

  run<T>(fn: (...args: any[]) => T): T {
    return this.zone.run(fn);
  }

  // If already in zone, then just invoke fn;
  // If not, then invoke fn inside NgZone.
  // The main use for this right now is if we are calling into a downgraded service from ajs
  // and we need to do some dynamic component creation in angular which needs to happen in the NgZone.
  // The ajs stuff may not be happening in the NgZone, so at the point where the dynamic component creation needs to happen,
  // we need to enter the NgZone for the change detection to work properly for the dynamic component.
  // This logic is kinda based on the wrapCallback function in downgrade_component.ts in angular.
  runIfNecessary<T>(fn: () => T): T {
    if (NgZone.isInAngularZone()) {
      return fn();
    }
    return this.zone.run(fn);
  }
}
